// AI Backend Service - Uses the main backend API
import axios from 'axios';

const API_BASE_URL = 'https://cgb-backend.carl-g-bisaillon.workers.dev/api';

// Create axios instance for AI API calls
const aiAxios = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds timeout for AI operations
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add response interceptor for error handling
aiAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('AI API Error:', error);
    return Promise.reject(error);
  }
);

// Content Generation API
export const generateContent = async (params) => {
  try {
    const response = await aiAxios.post('/ai/generate-content', {
      prompt: params.prompt,
      platform: params.platform || 'general',
      contentType: params.contentType || 'post',
      maxLength: params.maxLength || 280,
      includeHashtags: params.includeHashtags || false,
      includeEmojis: params.includeEmojis || true,
      userId: params.userId || 'dashboard-user'
    });
    return response.data;
  } catch (error) {
    console.error('Generate content error:', error);
    // Return fallback response
    return {
      success: false,
      generatedContent: `Voici du contenu pour ${params.platform || 'votre plateforme'} :

🎭 **"Préparez-vous à rire jusqu'à en avoir mal aux côtes !"**

📝 **Contenu suggéré :**
• Annoncez votre spectacle avec enthousiasme
• Utilisez des emojis pour attirer l'attention
• Posez des questions pour engager l'audience
• Partagez des anecdotes drôles

💡 **Conseil :** Adaptez le ton à votre audience et n'hésitez pas à être créatif !`,
      error: error.response?.data?.message || 'Service temporairement indisponible'
    };
  }
};

export const generateHashtags = async (params) => {
  try {
    const response = await aiAxios.post('/ai/generate-hashtags', {
      content: params.content,
      count: params.count || 10,
      platform: params.platform || 'general',
      userId: params.userId || 'dashboard-user'
    });
    return response.data;
  } catch (error) {
    console.error('Generate hashtags error:', error);
    // Return fallback hashtags
    return {
      success: false,
      hashtags: [
        '#LaSoireeDuRire',
        '#SpectacleHumour',
        '#RireAuxEclats',
        '#StandUpComedy',
        '#Drôle',
        '#Divertissement',
        '#SoireeComedy',
        '#Humeur',
        '#Blagues',
        '#ClubComedy'
      ],
      error: error.response?.data?.message || 'Service temporairement indisponible'
    };
  }
};

// Content Optimization API
export const optimizeContent = async (params) => {
  try {
    const response = await aiAxios.post('/ai/optimize-content', {
      content: params.content,
      platform: params.platform || 'general',
      targetAudience: params.targetAudience || 'general',
      goals: params.goals || ['engagement']
    });
    return response.data;
  } catch (error) {
    console.error('Optimize content error:', error);
    return {
      success: false,
      optimizedContent: params.content,
      suggestions: [
        'Ajoutez des emojis pour plus d\'engagement',
        'Posez une question à la fin',
        'Utilisez des mots-clés pertinents'
      ],
      error: error.response?.data?.message || 'Service temporairement indisponible'
    };
  }
};

export const analyzeSentiment = async (params) => {
  try {
    const response = await aiAxios.post('/ai/analyze-sentiment', {
      content: params.content,
      platform: params.platform || 'general'
    });
    return response.data;
  } catch (error) {
    console.error('Analyze sentiment error:', error);
    return {
      success: false,
      sentiment: 'neutral',
      confidence: 0.5,
      suggestions: ['Le contenu semble neutre'],
      error: error.response?.data?.message || 'Service temporairement indisponible'
    };
  }
};

// Analytics and Insights API
export const getInsights = async (params) => {
  try {
    const response = await aiAxios.post('/ai/insights', {
      query: params.query,
      dataType: params.dataType || 'analytics',
      platform: params.platform || 'general',
      userId: params.userId || 'dashboard-user'
    });
    return response.data;
  } catch (error) {
    console.error('Get insights error:', error);
    return {
      success: false,
      insights: [
        'Analysez vos métriques d\'engagement',
        'Identifiez vos meilleures heures de publication',
        'Testez différents types de contenu',
        'Interagissez avec votre audience'
      ],
      recommendations: [
        'Publiez plus de contenu engageant',
        'Répondez rapidement aux commentaires',
        'Utilisez des visuels attractifs'
      ],
      error: error.response?.data?.message || 'Service temporairement indisponible'
    };
  }
};

// Content Ideas API
export const generateContentIdeas = async (params) => {
  try {
    const response = await aiAxios.post('/ai/generate-content-ideas', {
      platform: params.platform || 'general',
      theme: params.theme || 'spectacle humour',
      count: params.count || 5,
      userId: params.userId || 'dashboard-user'
    });
    return response.data;
  } catch (error) {
    console.error('Generate content ideas error:', error);
    return {
      success: false,
      ideas: [
        'Anecdotes de coulisses du spectacle',
        'Présentation des comédiens',
        'Teasers des meilleurs moments',
        'Témoignages d\'audience',
        'Conseils d\'humour'
      ],
      error: error.response?.data?.message || 'Service temporairement indisponible'
    };
  }
};

// Posting Schedule API
export const suggestPostingTime = async (params) => {
  try {
    const response = await aiAxios.post('/ai/suggest-posting-time', {
      platform: params.platform || 'facebook',
      audience: params.audience || 'general',
      timezone: params.timezone || 'America/Montreal'
    });
    return response.data;
  } catch (error) {
    console.error('Suggest posting time error:', error);
    return {
      success: false,
      bestTimes: ['19h-21h', '12h-14h'],
      bestDays: ['Vendredi', 'Samedi'],
      reasoning: 'Basé sur les tendances générales des réseaux sociaux',
      error: error.response?.data?.message || 'Service temporairement indisponible'
    };
  }
};

// Export all functions as a single object for easier importing
export const aiAPI = {
  generateContent,
  generateHashtags,
  optimizeContent,
  analyzeSentiment,
  suggestPostingTime,
  getInsights,
  generateContentIdeas,
};

export default aiAPI;
